/**
 * Which control a parameter row gets, and whether what the planner typed is a value the platform
 * will accept — the half of the v7 parameter editing that the register (foundations) and Simulate
 * (inseason) share (bata-ui#196 §2.2, `docs/adr/9024`). Promoted from
 * `src/features/foundations/parameter-editing.ts`, whose module comment keeps the five rules; the
 * three that live here:
 *
 * - **The control follows the catalogue, never the key's name.** `controlFor` reads
 *   `ParameterKeyEntry` (`valueType`, `unit`, `range`, `vocabulary`) and nothing else.
 * - **Validation mirrors the platform** (bata-platform `services/repositories/simulation.py`
 *   `overlay_value`, and `parameter_resolution.validate_value` for the register): empty, padded,
 *   too long, not a finite decimal, an integer with a fraction, outside the published band, a
 *   boolean other than `0`/`1`, a text value outside the vocabulary. A `date` key is not
 *   overlayable. `sent` is the exact string the platform parses. The Italian decimal comma is
 *   accepted. Exponent notation (`1e3`) is refused; the length limit is the overlay's 128 in
 *   `scenario` mode and the register column's 200 in `registry` mode.
 * - **A share is typed in percent, always.** `85` sends `0.85`; `0,85` sends `0.0085` with the
 *   non-blocking warning `share_looks_like_fraction`.
 *
 * No React, no `window`, no network, no clock.
 */

import type { ParameterKeyEntry, ParameterRow, ValueBand } from '@/api/generated';

import { parsePricePointRule, PRICE_POINT_RULE_UNIT, RULE_REASON_KEY } from './price-point-rule';
import { doorKeys, parameterValue } from './values';

// ── the mode ─────────────────────────────────────────────────────────────────

/**
 * `registry`: a governed proposal from `/foundations/parameters`. `scenario`: a Simulate overlay,
 * which the platform checks with `overlay_value` and which never writes the register.
 */
export type EditMode = 'registry' | 'scenario';

/** bata-platform `simulation.MAX_OVERLAY_VALUE_LENGTH` (inseason's `simulate.ts` holds the same
 *  number; it is restated rather than imported because a cross-feature import fails the build). */
export const SCENARIO_MAX_VALUE_LENGTH = 128;

/** bata-platform `parameter.text_value`, `String(200)`: the longest value the register stores. */
export const REGISTRY_MAX_VALUE_LENGTH = 200;

export function maxValueLength(mode: EditMode): number {
  return mode === 'scenario' ? SCENARIO_MAX_VALUE_LENGTH : REGISTRY_MAX_VALUE_LENGTH;
}

// ── 1.1 which control a row gets ─────────────────────────────────────────────

/** Why a row offers no control. Each is said on the row, never shown as a disabled field. */
export type LockReason =
  /** The capabilities read (`useGovernedWrite('propose')`) closes the write for this person. */
  | 'no_permission'
  /** The row holds both value columns, or a value whose kind contradicts the catalogue's type. */
  | 'ambiguous'
  /** A `date` key in `scenario` mode: the platform's `overlay_value` refuses the type. */
  | 'not_overlayable'
  /** A `valueType` this build does not know: no control is guessed for it. */
  | 'unknown_value_type';

export type ParameterControl =
  /** `unit === 'share'` on a numeric key, typed in percent. `range` is in SHARE units (0.85). */
  | { readonly kind: 'share'; readonly mode: EditMode; readonly range: ValueBand | null }
  | { readonly kind: 'integer'; readonly mode: EditMode; readonly range: ValueBand | null; readonly unit: string | null }
  | { readonly kind: 'decimal'; readonly mode: EditMode; readonly range: ValueBand | null; readonly unit: string | null }
  /** `valueType === 'boolean'` or `unit === 'boolean'`; sent as `0` / `1`. */
  | { readonly kind: 'boolean'; readonly mode: EditMode }
  /** A published, non-empty `vocabulary`. */
  | { readonly kind: 'choice'; readonly mode: EditMode; readonly vocabulary: readonly string[] }
  /** `unit === 'code_list'` (e.g. `module.*.excluded_doors`): comma-separated, normalised. */
  | { readonly kind: 'codeList'; readonly mode: EditMode }
  /**
   * `unit === 'price_point_rule'` (`markdown.price_point_rule`, bata-platform ADR 6190): endings per
   * price band, checked as the platform checks it (`./price-point-rule.ts`) and sent canonical.
   */
  | { readonly kind: 'pricePointRule'; readonly mode: EditMode }
  /** `valueType === 'date'`, registry mode only: an ISO calendar date, stored as text. */
  | { readonly kind: 'date'; readonly mode: EditMode }
  | { readonly kind: 'text'; readonly mode: EditMode }
  | { readonly kind: 'locked'; readonly reason: LockReason };

/** Whether the control's value travels in `numericValue` (else in `textValue`). */
export function isNumericControl(control: ParameterControl): boolean {
  return control.kind === 'share' || control.kind === 'integer' || control.kind === 'decimal' || control.kind === 'boolean';
}

/** The band the catalogue publishes: `range` (the card) or `band` (the spec) — the same value. */
function publishedBand(entry: ParameterKeyEntry): ValueBand | null {
  return entry.range ?? entry.band ?? null;
}

/**
 * The control one row offers — decided from its catalogue entry alone.
 *
 * `row` is `null` for a key with no row in force (a first value). `writable` is the capabilities
 * read's answer for `propose` (registry) or for running a simulation (scenario); `false` locks
 * every row with `no_permission`, and is checked first because it applies to the whole table.
 */
export function controlFor(
  entry: ParameterKeyEntry,
  row: ParameterRow | null,
  mode: EditMode,
  writable: boolean,
): ParameterControl {
  if (!writable) return { kind: 'locked', reason: 'no_permission' };

  const control = baseControl(entry, mode);
  if (control.kind === 'locked' || row === null) return control;

  const current = parameterValue(row);
  if (current.kind === 'ambiguous') return { kind: 'locked', reason: 'ambiguous' };
  if (current.kind === 'numeric' && !isNumericControl(control)) return { kind: 'locked', reason: 'ambiguous' };
  if (current.kind === 'text' && isNumericControl(control)) return { kind: 'locked', reason: 'ambiguous' };
  return control;
}

function baseControl(entry: ParameterKeyEntry, mode: EditMode): ParameterControl {
  const unit = entry.unit ?? null;
  const range = publishedBand(entry);
  const vocabulary = entry.vocabulary ?? null;
  switch (entry.valueType) {
    case 'boolean':
      return { kind: 'boolean', mode };
    case 'integer':
      return unit === 'boolean' ? { kind: 'boolean', mode } : { kind: 'integer', mode, range, unit };
    case 'numeric':
      if (unit === 'boolean') return { kind: 'boolean', mode };
      if (unit === 'share') return { kind: 'share', mode, range };
      return { kind: 'decimal', mode, range, unit };
    case 'text':
      if (vocabulary !== null && vocabulary.length > 0) return { kind: 'choice', mode, vocabulary };
      if (unit === 'code_list') return { kind: 'codeList', mode };
      if (unit === PRICE_POINT_RULE_UNIT) return { kind: 'pricePointRule', mode };
      return { kind: 'text', mode };
    case 'date':
      return mode === 'scenario' ? { kind: 'locked', reason: 'not_overlayable' } : { kind: 'date', mode };
    default:
      return { kind: 'locked', reason: 'unknown_value_type' };
  }
}

// ── 1.2 checking a draft as it is typed ──────────────────────────────────────

/** What the planner typed, verbatim. A code list is its comma-separated text. */
export type RawDraft = string;

export type DraftRefusal =
  | 'empty'
  | 'padded'
  | 'too_long'
  | 'not_a_number'
  | 'ambiguous_grouping'
  | 'not_integer'
  | 'outside_range'
  | 'not_in_vocabulary'
  | 'not_boolean'
  | 'not_a_date'
  /** A price-point rule the platform would refuse; `hint` carries the rule's own reason. */
  | 'price_point_rule'
  | 'locked';

export type DraftWarning = 'share_looks_like_fraction';

export type DraftValue = number | string | boolean | readonly string[];

export type DraftCheck =
  /** `sent` is the exact string the platform parses (and the overlay carries). */
  | { readonly ok: true; readonly value: DraftValue; readonly sent: string; readonly warning: DraftWarning | null }
  /** `hint` is a corrected draft, in what the planner types (percent for a share), or `null`. */
  | { readonly ok: false; readonly reason: DraftRefusal; readonly hint: string | null };

const refused = (reason: DraftRefusal, hint: string | null = null): DraftCheck => ({ ok: false, reason, hint });

/**
 * A decimal as typed: optional sign, digits, one `.` or `,` separator. No exponent, no grouping —
 * `1.000,5` is refused rather than read as one of its two plausible meanings.
 */
const DECIMAL = /^([+-]?)(\d*)(?:[.,](\d*))?$/;

interface Decimal {
  readonly negative: boolean;
  readonly whole: string;
  readonly fraction: string;
}

function parseDecimal(text: string): Decimal | null {
  const match = DECIMAL.exec(text);
  if (match === null) return null;
  const whole = match[2] ?? '';
  const fraction = match[3] ?? '';
  if (whole === '' && fraction === '') return null;
  return { negative: match[1] === '-', whole, fraction };
}

/**
 * `1.000`, `1,000`, `12.500`: one to three whole digits, then exactly three after the separator.
 * An Italian or Latin-American planner writes a thousand as `1.000`, an English one as `1,000`,
 * and both are also a well-formed decimal meaning one — so the draft is refused rather than
 * silently read as the smaller number (`0.125` is not ambiguous: nobody groups a leading zero).
 */
function ambiguousGrouping(decimal: Decimal): boolean {
  return /^[1-9]\d{0,2}$/.test(decimal.whole) && decimal.fraction.length === 3;
}

/** The decimal as a canonical string: no leading or trailing zeros, `.` separator, no `-0`. */
function canonical(decimal: Decimal): string {
  const whole = decimal.whole.replace(/^0+/, '') || '0';
  const fraction = decimal.fraction.replace(/0+$/, '');
  const magnitude = fraction === '' ? whole : `${whole}.${fraction}`;
  return decimal.negative && magnitude !== '0' ? `-${magnitude}` : magnitude;
}

/** Moves the separator `places` digits (negative = left), exactly — no floating point. */
function shift(decimal: Decimal, places: number): Decimal {
  const digits = decimal.whole + decimal.fraction;
  const point = decimal.whole.length + places;
  if (point <= 0) return { negative: decimal.negative, whole: '0', fraction: '0'.repeat(-point) + digits };
  if (point >= digits.length) return { negative: decimal.negative, whole: digits + '0'.repeat(point - digits.length), fraction: '' };
  return { negative: decimal.negative, whole: digits.slice(0, point), fraction: digits.slice(point) };
}

/** A published bound as a decimal the planner would type (percent for a share). */
function boundAsTyped(bound: number, percent: boolean): string {
  const text = Number.isInteger(bound) ? String(bound) : bound.toFixed(12);
  const decimal = parseDecimal(text);
  if (decimal === null) return String(bound);
  return canonical(percent ? shift(decimal, 2) : decimal);
}

/** A real calendar date, `YYYY-MM-DD`. */
function isIsoDate(text: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return false;
  const [year, month, day] = text.split('-').map(Number) as [number, number, number];
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

/**
 * Whether `raw` is a value the platform will accept for this control, and what will be sent.
 *
 * The order of the first three checks is the platform's (`overlay_value`): length, padding and
 * emptiness are refused before the value is typed. A code list is normalised as the platform
 * stores it (`doorKeys`: trimmed, de-duplicated, sorted), so padding inside it is not a refusal —
 * and an empty list is a real value ("no door excluded") in the register, though not in an
 * overlay, which refuses an empty string.
 */
export function parseDraft(control: ParameterControl, raw: RawDraft): DraftCheck {
  if (control.kind === 'locked') return refused('locked');
  const limit = maxValueLength(control.mode);

  if (control.kind === 'codeList') {
    const doors = doorKeys(raw);
    const sent = doors.join(',');
    if (sent === '' && control.mode === 'scenario') return refused('empty');
    if (sent.length > limit) return refused('too_long');
    return { ok: true, value: doors, sent, warning: null };
  }

  if (raw.trim() === '') return refused('empty');
  if (raw !== raw.trim()) return refused('padded');
  if (raw.length > limit) return refused('too_long');

  switch (control.kind) {
    case 'text':
      return { ok: true, value: raw, sent: raw, warning: null };
    case 'choice':
      return control.vocabulary.includes(raw)
        ? { ok: true, value: raw, sent: raw, warning: null }
        : refused('not_in_vocabulary');
    case 'date':
      return isIsoDate(raw) ? { ok: true, value: raw, sent: raw, warning: null } : refused('not_a_date');
    case 'pricePointRule': {
      const rule = parsePricePointRule(raw);
      if (!rule.ok) return refused('price_point_rule', rule.reason);
      return rule.text.length > limit ? refused('too_long') : { ok: true, value: rule.text, sent: rule.text, warning: null };
    }
    case 'boolean':
      return raw === '0' || raw === '1'
        ? { ok: true, value: raw === '1', sent: raw, warning: null }
        : refused('not_boolean');
    case 'share':
      return parseShare(control.range, raw, limit);
    case 'integer':
    case 'decimal':
      return parseNumber(control.kind, control.range, raw, limit);
  }
}

/**
 * Whether `value` is outside the band. A band with no `upper` is a FLOOR with no ceiling
 * (bata-platform#470 §3, its ADR 9017): the platform omits `upper` rather than inventing one, so
 * nothing above the floor is refused here either.
 */
export function outsideBand(range: ValueBand, value: number): boolean {
  return belowLower(range, value) || aboveUpper(range, value);
}

/**
 * Whether the band's lower bound is itself refused. `exclusive` is both bounds (an open interval,
 * #470 §2 follow-up); `lowerExclusive` is this bound alone (a half-open band such as `(0, 1]`,
 * bata-platform ADR 9017 amended 2026-10-20), so either one is honoured.
 */
export function lowerExcluded(range: ValueBand): boolean {
  return range.exclusive === true || range.lowerExclusive === true;
}

/**
 * Whether the band's upper bound is itself refused: only `exclusive` refuses it. The contract
 * (0.99) publishes no flag for the upper bound alone, so a band is closed, open, or `(lower, upper]`.
 */
export function upperExcluded(range: ValueBand): boolean {
  return range.exclusive === true;
}

function belowLower(range: ValueBand, value: number): boolean {
  return lowerExcluded(range) ? value <= range.lower : value < range.lower;
}

function aboveUpper(range: ValueBand, value: number): boolean {
  if (range.upper === undefined) return false;
  return upperExcluded(range) ? value >= range.upper : value > range.upper;
}

/** The bound `value` broke: the floor at or below it, else the ceiling above it. */
function breachedBound(range: ValueBand, value: number): number {
  return belowLower(range, value) || range.upper === undefined ? range.lower : range.upper;
}

function parseNumber(kind: 'integer' | 'decimal', range: ValueBand | null, raw: string, limit: number): DraftCheck {
  const decimal = parseDecimal(raw);
  if (decimal === null) return refused('not_a_number');
  if (ambiguousGrouping(decimal)) return refused('ambiguous_grouping');
  const sent = canonical(decimal);
  const value = Number(sent);
  if (!Number.isFinite(value)) return refused('not_a_number');
  if (kind === 'integer' && !Number.isInteger(value)) return refused('not_integer', String(Math.round(value)));
  if (range !== null && outsideBand(range, value)) {
    return refused('outside_range', boundAsTyped(breachedBound(range, value), false));
  }
  if (sent.length > limit) return refused('too_long');
  return { ok: true, value, sent, warning: null };
}

function parseShare(range: ValueBand | null, raw: string, limit: number): DraftCheck {
  const percentMatch = /^(.*?)\s*%$/.exec(raw);
  const typed = percentMatch === null ? raw : percentMatch[1] ?? '';
  const decimal = parseDecimal(typed);
  if (decimal === null) return refused('not_a_number');
  if (ambiguousGrouping(decimal)) return refused('ambiguous_grouping');
  const typedValue = Number(canonical(decimal));
  if (!Number.isFinite(typedValue)) return refused('not_a_number');
  const sent = canonical(shift(decimal, -2));
  const value = Number(sent);
  if (range !== null && outsideBand(range, value)) {
    return refused('outside_range', boundAsTyped(breachedBound(range, value), true));
  }
  if (sent.length > limit) return refused('too_long');
  const warning: DraftWarning | null = percentMatch === null && typedValue > 0 && typedValue < 1
    ? 'share_looks_like_fraction'
    : null;
  return { ok: true, value, sent, warning };
}

// ── the messages a draft check shows ─────────────────────────────────────────

/** The `outside_range` sentence for a band that is a floor with no ceiling (#470 §3). */
export const DRAFT_BELOW_FLOOR_KEY = 'parameterEdit.draft.refusal.belowFloor';

/**
 * The `outside_range` sentences for a band with a refused bound (bata-platform ADR 9017, amended
 * 2026-10-20): "greater than {lower}" / "less than {upper}" where the broken bound is itself refused,
 * and "at most {upper}" for an admitted ceiling whose floor is refused — never "between 0% and
 * 100%" for a band that refuses 0%.
 */
export const DRAFT_NOT_ABOVE_LOWER_KEY = 'parameterEdit.draft.refusal.mustExceedLower';
export const DRAFT_NOT_BELOW_UPPER_KEY = 'parameterEdit.draft.refusal.mustStayBelowUpper';
export const DRAFT_ABOVE_CEILING_KEY = 'parameterEdit.draft.refusal.aboveCeiling';

/**
 * The sentence for the one bound a draft broke, for a floor with no ceiling or a band with a refused
 * bound. `hint` is that bound as typed (`breachedBound`), so it names which end was broken.
 */
function boundMessage(range: ValueBand, percent: boolean, hint: string | null): DraftMessage {
  const lower = boundAsTyped(range.lower, percent);
  if (range.upper === undefined || hint === lower) {
    return lowerExcluded(range)
      ? { key: DRAFT_NOT_ABOVE_LOWER_KEY, values: { lower } }
      : { key: DRAFT_BELOW_FLOOR_KEY, values: { lower } };
  }
  const upper = boundAsTyped(range.upper, percent);
  return upperExcluded(range)
    ? { key: DRAFT_NOT_BELOW_UPPER_KEY, values: { upper } }
    : { key: DRAFT_ABOVE_CEILING_KEY, values: { upper } };
}

/** Each refusal's sentence, spelled out for the catalogue check. */
export const DRAFT_REFUSAL_KEY: Readonly<Record<DraftRefusal, string>> = {
  empty: 'parameterEdit.draft.refusal.empty',
  padded: 'parameterEdit.draft.refusal.padded',
  too_long: 'parameterEdit.draft.refusal.tooLong',
  not_a_number: 'parameterEdit.draft.refusal.notANumber',
  ambiguous_grouping: 'parameterEdit.draft.refusal.ambiguousGrouping',
  not_integer: 'parameterEdit.draft.refusal.notInteger',
  outside_range: 'parameterEdit.draft.refusal.outsideRange',
  not_in_vocabulary: 'parameterEdit.draft.refusal.notInVocabulary',
  not_boolean: 'parameterEdit.draft.refusal.notBoolean',
  not_a_date: 'parameterEdit.draft.refusal.notADate',
  price_point_rule: 'parameterEdit.draft.refusal.pricePointRule',
  locked: 'parameterEdit.draft.refusal.locked',
};

export const DRAFT_WARNING_KEY: Readonly<Record<DraftWarning, string>> = {
  share_looks_like_fraction: 'parameterEdit.draft.warning.shareLooksLikeFraction',
};

export interface DraftMessage {
  readonly key: string;
  readonly values: Readonly<Record<string, string>>;
}

/**
 * The line under the control for a refused draft or a warned one — `null` for a clean value, whose
 * line is `consequenceFor`'s. The values are canonical decimals (`.` separator) for the screen to
 * format in the reader's locale; a share's bounds are in percent, because that is what is typed.
 */
export function draftMessage(control: ParameterControl, raw: RawDraft, check: DraftCheck): DraftMessage | null {
  if (check.ok) {
    if (check.warning === null) return null;
    const typed = parseDecimal(raw);
    return {
      key: DRAFT_WARNING_KEY[check.warning],
      values: { typed: typed === null ? raw : canonical(typed), suggested: typed === null ? raw : canonical(shift(typed, 2)) },
    };
  }
  const key = DRAFT_REFUSAL_KEY[check.reason];
  switch (check.reason) {
    case 'too_long':
      return { key, values: { max: String(control.kind === 'locked' ? REGISTRY_MAX_VALUE_LENGTH : maxValueLength(control.mode)) } };
    case 'not_integer':
      return { key, values: { hint: check.hint ?? '' } };
    case 'outside_range': {
      const range = control.kind === 'share' || control.kind === 'integer' || control.kind === 'decimal' ? control.range : null;
      const percent = control.kind === 'share';
      if (range !== null && (range.upper === undefined || lowerExcluded(range) || upperExcluded(range))) {
        return boundMessage(range, percent, check.hint);
      }
      return {
        key,
        values: range === null || range.upper === undefined
          ? { lower: '', upper: '' }
          : { lower: boundAsTyped(range.lower, percent), upper: boundAsTyped(range.upper, percent) },
      };
    }
    case 'ambiguous_grouping': {
      // The two readings, as canonical decimals: `thousands` drops the separator, `decimal` keeps it.
      const typed = parseDecimal(raw.replace(/\s*%$/, ''));
      if (typed === null) return { key, values: { thousands: raw, decimal: raw } };
      return {
        key,
        values: { thousands: canonical({ ...typed, whole: typed.whole + typed.fraction, fraction: '' }), decimal: canonical(typed) },
      };
    }
    case 'not_in_vocabulary':
      return { key, values: { values: control.kind === 'choice' ? control.vocabulary.join(', ') : '' } };
    case 'price_point_rule': {
      // The rule's own reason, as the platform would name it; the drawer says which band.
      const rule = parsePricePointRule(raw);
      return rule.ok ? { key, values: {} } : { key: RULE_REASON_KEY[rule.reason], values: {} };
    }
    default:
      return { key, values: {} };
  }
}
